import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, BookOpen, MessageCircleQuestion, CheckCircle2, Trash2, Paperclip, ImageIcon, GripHorizontal, Mic } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import polyConfig from '../../poly.json';

const SUPPORT_CATEGORIES = [
  { id: 'custom_domain', label: '🌍 Custom Domain Setup' },
  { id: 'billing', label: '💳 Billing & Subscription' },
  { id: 'login', label: '🔐 Login & Account Access' },
  { id: 'design', label: '🎨 Profile Design & Layout' },
  { id: 'analytics', label: '📊 Analytics & Traffic Data' },
  { id: 'links', label: '🔗 Links & Integrations' },
  { id: 'security', label: '🚩 Report Abuse / Security' },
  { id: 'other', label: '❓ Something Else (Other)' },
];

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = error => reject(error);
});

/* ── Seasonal color palette ───────────────────────────────────────────────── */
const SEASON_STOPS = [
  // 🌸 Spring: Innovation & Growth
  { r: 252, g: 211, b: 77 },  // 0%   - Amber Bud (FCD34D)
  { r: 163, g: 230, b: 53 },  // 12%  - Lime Chartreuse (A3E635)
  { r: 74, g: 222, b: 128 },  // 24%  - Mint (4ADE80)
  
  // ☀️ Summer: Trust & Stability
  { r: 21, g: 128, b: 61 },   // 36%  - Deep Emerald (15803D)
  { r: 20, g: 83, b: 45 },    // 48%  - Forest Green (14532D)
  { r: 100, g: 116, b: 139 }, // 60%  - Slate/Silvery-Green (64748B)

  // 🍂 Autumn: The Data Harvest
  { r: 180, g: 83, b: 9 },    // 70%  - Amber/Gold (B45309)
  { r: 194, g: 65, b: 12 },   // 80%  - Rust (C2410C)
  { r: 153, g: 27, b: 27 },   // 88%  - Crimson (991B1B)

  // ❄️ Winter: Persistence & Structure
  { r: 120, g: 53, b: 15 },   // 94%  - Dark Copper (78350F)
  { r: 69, g: 26, b: 3 },     // 97%  - Espresso (451A03)
  { r: 51, g: 65, b: 85 },    // 100% - Slate Gray (334155)
];

function getSeasonalColor(progress) {
  const p = Math.max(0, Math.min(1, progress));
  const segCount = SEASON_STOPS.length - 1;
  const seg = Math.min(Math.floor(p * segCount), segCount - 1);
  const t = (p * segCount) - seg;
  const a = SEASON_STOPS[seg];
  const b = SEASON_STOPS[seg + 1];
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bl = Math.round(a.b + (b.b - a.b) * t);
  return `rgb(${r},${g},${bl})`;
}

/* ── Oak Leaf SVG — standalone, natural rounded shape ─────────────────────── */
const OakIcon = ({ color = '#4ADE80', className = '' }) => (
  <svg className={className} viewBox="0 0 32 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Stem */}
    <path d="M16 42 L16 36" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    
    {/* Main Leaf Body with rounded lobes and deep sinuses */}
    <path
      d="M16 36
         C11 38, 5 35, 6 29
         C7 26, 11 27, 12 25
         C5 24, 3 17, 6 13
         C8 10, 12 13, 13 11
         C11 5, 14 2, 16 2
         C18 2, 21 5, 19 11
         C20 13, 24 10, 26 13
         C29 17, 27 24, 20 25
         C21 27, 25 26, 26 29
         C27 35, 21 38, 16 36 Z"
      fill={color}
    />

    {/* Veins for texture and structure */}
    {/* Midrib */}
    <path d="M16 36 Q15 18, 16 4" stroke="rgba(0,0,0,0.15)" strokeWidth="1.2" strokeLinecap="round" />
    {/* Bottom lobes */}
    <path d="M16 31 Q11 31, 7 32" stroke="rgba(0,0,0,0.12)" strokeWidth="0.8" strokeLinecap="round" />
    <path d="M16 31 Q21 31, 25 32" stroke="rgba(0,0,0,0.12)" strokeWidth="0.8" strokeLinecap="round" />
    {/* Middle lobes */}
    <path d="M15.5 22 Q9 20, 5 18" stroke="rgba(0,0,0,0.12)" strokeWidth="0.8" strokeLinecap="round" />
    <path d="M16.5 22 Q23 20, 27 18" stroke="rgba(0,0,0,0.12)" strokeWidth="0.8" strokeLinecap="round" />
    {/* Top lobes */}
    <path d="M15.5 12 Q12 9, 12 5" stroke="rgba(0,0,0,0.12)" strokeWidth="0.8" strokeLinecap="round" />
    <path d="M16.5 12 Q20 9, 20 5" stroke="rgba(0,0,0,0.12)" strokeWidth="0.8" strokeLinecap="round" />
    
    {/* Subtle natural highlight for volumetric depth */}
    <path
      d="M16 36 C11 38, 5 35, 6 29 C7 26, 11 27, 12 25 C5 24, 3 17, 6 13 C8 10, 12 13, 13 11 C11 5, 14 2, 16 2 C18 2, 21 5, 19 11 C20 13, 24 10, 26 13 C29 17, 27 24, 20 25 C21 27, 25 26, 26 29 C27 35, 21 38, 16 36 Z"
      fill="url(#oakHL)"
    />
    <defs>
      <radialGradient id="oakHL" cx="0.35" cy="0.3" r="0.6">
        <stop offset="0%" stopColor="rgba(255,255,255,0.25)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
      </radialGradient>
    </defs>
  </svg>
);

/* ── Small oak for chat header ────────────────────────────────────────────── */
const OakIconSmall = ({ color = '#FCD34D', className = '' }) => (
  <svg className={className} viewBox="0 0 32 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 42 L16 36" stroke={color} strokeWidth="2.5" strokeLinecap="round" style={{ filter: 'brightness(0.7)' }} />
    <path
      d="M16 36 C11 38, 5 35, 6 29 C7 26, 11 27, 12 25 C5 24, 3 17, 6 13 C8 10, 12 13, 13 11 C11 5, 14 2, 16 2 C18 2, 21 5, 19 11 C20 13, 24 10, 26 13 C29 17, 27 24, 20 25 C21 27, 25 26, 26 29 C27 35, 21 38, 16 36 Z"
      fill={color}
    />
    <path d="M16 36 Q15 18, 16 4" stroke="rgba(0,0,0,0.15)" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const formatMessageText = (text) => {
  if (typeof text !== 'string') return text;
  const urlRegex = /((?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+(?:\/[^\s.,?!]*)?)/gi;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      const href = part.startsWith('http') ? part : `https://${part}`;
      return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-500 font-bold hover:underline hover:text-indigo-600 transition-colors">{part}</a>;
    }
    return part;
  });
};

export default function RagChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [leafColor, setLeafColor] = useState('rgb(252, 211, 77)'); // Starts at Spring Amber Bud
  const [messages, setMessages] = useState([]);
  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState('rag'); // 'rag', 'ticket_category', 'ticket_identifier', 'ticket_description', 'ticket_submitted'
  const [ticketCategory, setTicketCategory] = useState('');
  const [ticketIdentifier, setTicketIdentifier] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [chatAttachments, setChatAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const rafRef = useRef(null);
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  
  const DEFAULT_GREETING = "Hi there! I'm the Linkit Assistant. How can I help you with your profile today?";

  // Keep a stable ref to processQuery to avoid stale closures in SpeechRecognition callbacks
  const processQueryRef = useRef(null);
  const currentTranscriptRef = useRef('');

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert("Voice recognition is not supported in this browser.");
        return;
      }

      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      currentTranscriptRef.current = '';

      recognition.onresult = (event) => {
        let interim = '';
        let final = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) final += event.results[i][0].transcript;
          else interim += event.results[i][0].transcript;
        }

        const currentText = final || interim;
        currentTranscriptRef.current = currentText;
        setInput(currentText);

        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);

        if (currentText.trim()) {
          silenceTimerRef.current = setTimeout(() => {
            recognition.stop();
          }, 1800);
        }
      };

      recognition.onerror = (e) => {
        console.error("Speech Recognition Error:", e.error);
        if (e.error === 'not-allowed') {
          alert("Microphone access denied. Please allow microphone permissions in your browser.");
        }
      };

      recognition.onend = () => {
        setIsListening(false);
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        
        if (currentTranscriptRef.current.trim() && processQueryRef.current) {
          const query = currentTranscriptRef.current;
          currentTranscriptRef.current = '';
          setInput('');
          processQueryRef.current(query, [], true);
        }
      };

      try {
        setInput('');
        recognition.start();
        setIsListening(true);
      } catch (e) {
        console.error("Failed to start speech recognition:", e);
      }
    }
  };

  const speakResponse = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const msg = new SpeechSynthesisUtterance(text);
      msg.rate = 1.0;
      window.speechSynthesis.speak(msg);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      let loadedMessages = [];
      let profileData = null;

      if (session?.user) {
        const { data } = await supabase.from('profiles').select('full_name, username, user_id, chatbot_history').eq('user_id', session.user.id).single();
        if (data) {
          profileData = data;
          setUserProfile({ ...data, email: session.user.email });
          if (data.chatbot_history && Array.isArray(data.chatbot_history) && data.chatbot_history.length > 0) {
            loadedMessages = data.chatbot_history;
          }
        }
      } else {
        const local = localStorage.getItem('linkit_chatbot_history');
        if (local) {
          try { loadedMessages = JSON.parse(local); } catch (e) {}
        }
      }

      if (loadedMessages.length === 0) {
        const greeting = profileData 
          ? `Hi ${profileData.full_name || profileData.username || 'there'}! I'm the Linkit Assistant. How can I help you with your profile today?`
          : DEFAULT_GREETING;
        loadedMessages = [{ role: 'ai', content: greeting, sources: 0 }];
      }

      setMessages(loadedMessages);
      setIsHistoryLoaded(true);
    });
  }, []);

  // Sync messages
  useEffect(() => {
    if (!isHistoryLoaded) return;
    localStorage.setItem('linkit_chatbot_history', JSON.stringify(messages));
    if (userProfile?.user_id) {
      supabase.from('profiles').update({ chatbot_history: messages }).eq('user_id', userProfile.user_id).then();
    }
  }, [messages, isHistoryLoaded, userProfile]);

  const handleClearChat = async () => {
    const greeting = userProfile 
      ? `Hi ${userProfile.full_name || userProfile.username || 'there'}! I'm the Linkit Assistant. How can I help you with your profile today?`
      : DEFAULT_GREETING;
      
    const resetMsg = [{ role: 'ai', content: greeting, sources: 0 }];
    setMessages(resetMsg);
    setChatMode('rag');
    
    localStorage.removeItem('linkit_chatbot_history');
    if (userProfile?.user_id) {
      await supabase.from('profiles').update({ chatbot_history: resetMsg }).eq('user_id', userProfile.user_id);
    }
  };


  /* ── Scroll-driven seasonal color ────────────────────────────────────── */
  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? scrollTop / docHeight : 0;
      setLeafColor(getSeasonalColor(progress));
      rafRef.current = null;
    });
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleScroll]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 400);
      scrollToBottom();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    
    let validFiles = [];
    for (const file of files) {
      if (file.size > 2 * 1024 * 1024) {
         alert(`File ${file.name} is over 2MB limit.`);
      } else {
         validFiles.push(file);
      }
    }
    
    if (chatAttachments.length + validFiles.length > 3) {
      alert("You can attach a maximum of 3 files.");
      validFiles = validFiles.slice(0, 3 - chatAttachments.length);
    }

    setChatAttachments(prev => [...prev, ...validFiles]);
  };
  
  const removeAttachment = (idx) => {
    setChatAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const processQuery = async (queryText, currentAttachments, isVoiceQuery = false) => {
    if ((!queryText.trim() && currentAttachments.length === 0) || isLoading) return;
    setIsLoading(true);

    const encodedAttachments = await Promise.all(currentAttachments.map(async (f) => ({
      name: f.name,
      type: f.type,
      size: f.size,
      data: await fileToBase64(f)
    })));

    const newUserMsg = { 
      role: 'user', 
      content: queryText || 'Sent an attachment.',
      attachments: encodedAttachments 
    };
    
    const nextMessages = [...messages, newUserMsg];
    setMessages(nextMessages);

    if (chatMode === 'ticket_identifier') {
      setTicketIdentifier(queryText);
      setChatMode('ticket_description');
      setMessages(prev => [
        ...prev,
        { role: 'ai', content: 'Thanks! Please describe your issue in detail. You can attach any relevant screenshots or files.' }
      ]);
      setIsLoading(false);
      return;
    }

    if (chatMode === 'ticket_description') {
      try {
        const res = await fetch('/api/support?action=ticket', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            [polyConfig.HEADER_NAME]: polyConfig.HEADER_VALUE,
          },
          body: JSON.stringify({
            identifier: ticketIdentifier,
            category: ticketCategory,
            source: 'chatbot',
            chatHistory: nextMessages,
          }),
        });
        if (!res.ok) throw new Error('Ticket failed');
        setChatMode('ticket_submitted');
      } catch (err) {
        setMessages(prev => [...prev, { role: 'ai', content: 'Sorry, we could not submit your ticket. Please try the support page.' }]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      const historyForBackend = nextMessages.filter(
        msg => !msg.content.includes('having trouble connecting right now') && !msg.content.includes('API Error') && !msg.content.includes('could not submit your ticket')
      );

      const response = await fetch('/api/rag?action=query', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          [polyConfig.HEADER_NAME]: polyConfig.HEADER_VALUE,
        },
        body: JSON.stringify({ query: queryText, action: 'query', chatHistory: historyForBackend }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'API Error');
      }
      const data = await response.json();

      setMessages(prev => [
        ...prev, 
        { role: 'ai', content: data.answer, sources: data.sources_consulted }
      ]);
      if (isVoiceQuery) speakResponse(data.answer);
    } catch (err) {
      const errMsg = err.message || 'Sorry, I am having trouble connecting right now. Please try again in a moment.';
      setMessages(prev => [
        ...prev, 
        { role: 'ai', content: errMsg }
      ]);
      if (isVoiceQuery) speakResponse(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    processQueryRef.current = processQuery;
  }, [processQuery]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const query = input;
    setInput('');
    const attachments = [...chatAttachments];
    setChatAttachments([]);
    processQuery(query, attachments, false);
  };

  const handleStartTicket = () => {
    setChatMode('ticket_category');
    setMessages(prev => [...prev, { role: 'ai', content: 'I can help you open a support ticket. What kind of issue is this?' }]);
  };

  const handleCategorySelect = async (categoryId) => {
    const cat = SUPPORT_CATEGORIES.find(c => c.id === categoryId);
    setTicketCategory(cat.label);
    
    setMessages(prev => [
      ...prev,
      { role: 'user', content: cat.label }
    ]);

    if (userProfile) {
      setTicketIdentifier(userProfile.email || userProfile.username);
      setChatMode('ticket_description');
      setMessages(prev => [
        ...prev,
        { role: 'ai', content: 'Please describe your issue in detail. You can attach any relevant screenshots or files.' }
      ]);
    } else {
      setChatMode('ticket_identifier');
      setMessages(prev => [
        ...prev,
        { role: 'ai', content: 'Got it. Are you a registered Linkit user? Type your exact @username. If not, type your email address.' }
      ]);
    }
  };

  return (
    <motion.div 
      className="fixed bottom-6 right-6 z-[90] font-sans flex flex-col items-end"
      drag
      dragMomentum={false}
      dragElastic={0}
      whileDrag={{ scale: 1.02 }}
    >
      
      {/* ── Chat Panel ──────────────────────────────────────────────── */}
      <div className={`rag-panel ${isOpen ? 'rag-panel--open' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 cursor-grab active:cursor-grabbing" style={{ background: '#3E3C38' }}>
          <div className="flex items-center gap-3">
            <OakIconSmall color={leafColor} className="w-7 h-7 flex-shrink-0" />
            <div>
              <h3 className="text-[15px] font-semibold text-[#FAF8F5] tracking-tight">Linkit Support</h3>
              <p className="text-[11px] text-[#A49E93] mt-0.5">Always here to help</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <GripHorizontal className="w-4 h-4 opacity-40 text-white mr-1" />
            {chatMode === 'rag' && (
              <>
                <button 
                  onClick={handleClearChat}
                  title="Clear Chat"
                  className="p-1.5 rounded-full text-[#A49E93] hover:text-[#FAF8F5] hover:bg-white/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={handleStartTicket}
                  title="Contact Human Support"
                  className="p-1.5 rounded-full text-[#A49E93] hover:text-[#FAF8F5] hover:bg-white/10 transition-colors"
                >
                  <MessageCircleQuestion className="w-4 h-4" />
                </button>
              </>
            )}
            <button 
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-full text-[#A49E93] hover:text-[#FAF8F5] hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 flex flex-col rag-scrollbar" style={{ background: '#FAF8F5' }}>
          {messages.map((msg, idx) => (
            <div 
              key={idx} 
              className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} max-w-[85%] ${msg.role === 'user' ? 'self-end' : 'self-start'}`}
            >
              {msg.attachments && msg.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {msg.attachments.map((att, i) => (
                    <div key={i} className="relative group rounded-lg overflow-hidden border border-[#E8E4D9] bg-white">
                      {att.type.startsWith('image/') ? (
                        <img src={att.data} alt={att.name} className="h-16 w-auto object-cover max-w-full" />
                      ) : (
                        <div className="flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold text-[#3E3C38]">
                          <Paperclip size={12} /> <span className="truncate max-w-[100px]">{att.name}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div 
                className={`
                  px-4 py-3 text-[13.5px] leading-relaxed
                  ${msg.role === 'user' 
                    ? 'text-[#FAF8F5] rounded-[18px] rounded-br-[4px]' 
                    : 'bg-white border border-[#E8E4D9] text-[#4A4742] rounded-[18px] rounded-bl-[4px] shadow-sm'
                  }
                `}
                style={msg.role === 'user' ? { background: '#3E3C38' } : undefined}
              >
                {formatMessageText(msg.content)}
              </div>
              {msg.role === 'ai' && msg.sources > 0 && (
                <div className="flex items-center gap-1 mt-1.5 ml-1 px-2 py-0.5 text-[11px] font-medium text-[#A49E93]">
                  <BookOpen className="w-3 h-3" />
                  Referenced from docs
                </div>
              )}
            </div>
          ))}

          {chatMode === 'ticket_category' && !isLoading && (
            <div className="flex flex-col gap-2 self-start max-w-[90%] mt-2">
              {SUPPORT_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat.id)}
                  className="px-4 py-2 text-[13px] text-left text-[#3E3C38] bg-white border border-[#E8E4D9] rounded-[12px] shadow-sm hover:border-[#B5AFA5] hover:bg-[#F9F9F9] transition-all"
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}

          {chatMode === 'ticket_submitted' && (
            <div className="self-start px-4 py-3 bg-[#E8F5E9] border border-[#A5D6A7] text-[#2E7D32] rounded-[18px] rounded-bl-[4px] shadow-sm flex items-start gap-3 mt-2">
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div className="text-[13.5px] leading-relaxed">
                All set! I've sent your request and chat history to the Linkit support team. They typically respond within 24 hours.
              </div>
            </div>
          )}
          
          {isLoading && (
            <div className="self-start px-4 py-3 bg-white border border-[#E8E4D9] rounded-[18px] rounded-bl-[4px] shadow-sm">
              <div className="flex items-center gap-2 h-5">
                <div className="w-1.5 h-1.5 bg-[#B5AFA5] rounded-full animate-bounce [animation-delay:-0.3s]" />
                <div className="w-1.5 h-1.5 bg-[#B5AFA5] rounded-full animate-bounce [animation-delay:-0.15s]" />
                <div className="w-1.5 h-1.5 bg-[#B5AFA5] rounded-full animate-bounce" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 bg-white border-t border-[#E8E4D9]">
          {chatAttachments.length > 0 && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar mb-3">
              {chatAttachments.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F4F1EA] text-[11px] font-bold text-[#3E3C38] rounded-full shrink-0 max-w-[150px] border border-[#E8E4D9]">
                  <ImageIcon size={12} className="text-[#A49E93] shrink-0" />
                  <span className="truncate">{f.name}</span>
                  <button type="button" onClick={() => removeAttachment(i)} className="text-[#A49E93] hover:text-[#FF6B6B] shrink-0 p-0.5"><X size={10} strokeWidth={3} /></button>
                </div>
              ))}
            </div>
          )}
          <form 
            onSubmit={handleSubmit}
            className="flex items-center gap-2 rounded-full p-1 pl-2 border border-[#E8E4D9] focus-within:border-[#B5AFA5] transition-colors"
            style={{ background: '#FAF8F5' }}
          >
            {chatMode === 'ticket_description' && (
              <label className="flex items-center justify-center w-8 h-8 rounded-full text-[#A49E93] hover:text-[#3E3C38] hover:bg-white transition-colors cursor-pointer shrink-0">
                <Paperclip className="w-4 h-4" />
                <input type="file" multiple className="sr-only" onChange={handleAttachmentChange} accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,text/csv,application/json" disabled={isLoading} />
              </label>
            )}
            <button
              type="button"
              onClick={toggleListening}
              className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors shrink-0 ${isListening ? 'text-red-500 bg-red-100 animate-pulse' : 'text-[#A49E93] hover:text-[#3E3C38] hover:bg-white'}`}
              title="Voice Input"
            >
              <Mic className="w-4 h-4" />
            </button>
            <input

              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                isListening ? "Listening... (speak now)" :
                chatMode === 'ticket_identifier' ? "Type your @username or email..." :
                chatMode === 'ticket_description' ? "Describe your issue..." :
                chatMode === 'ticket_submitted' ? "Ticket submitted." :
                chatMode === 'ticket_category' ? "Please select a category above..." :
                "Ask a question..."
              }
              className="flex-1 bg-transparent text-[13.5px] text-[#3E3C38] placeholder:text-[#B5AFA5] focus:outline-none"
              disabled={isLoading || chatMode === 'ticket_category' || chatMode === 'ticket_submitted'}
            />
            <button 
              type="submit"
              disabled={(!input.trim() && chatAttachments.length === 0) || isLoading || chatMode === 'ticket_category' || chatMode === 'ticket_submitted'}
              className="flex items-center justify-center w-8 h-8 rounded-full text-white disabled:opacity-40 transition-all hover:opacity-90 active:scale-95 shrink-0"
              style={{ background: '#3E3C38' }}
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </form>
        </div>
      </div>

      {/* ── FAB — just the leaf, no container ────────────────────────── */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`rag-fab ${isOpen ? 'rag-fab--hidden' : ''}`}
        aria-label="Open chat"
      >
        <OakIcon color={leafColor} className="rag-fab__leaf" />
      </button>

      <style dangerouslySetInnerHTML={{__html: `
        /* ── Scrollbar ─────────────────────────────────────────────── */
        .rag-scrollbar::-webkit-scrollbar { width: 3px; }
        .rag-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .rag-scrollbar::-webkit-scrollbar-thumb { background: #E8E4D9; border-radius: 3px; }

        /* ── Chat Panel ────────────────────────────────────────────── */
        .rag-panel {
          margin-bottom: 0.75rem;
          width: 360px;
          max-width: calc(100vw - 3rem);
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transform-origin: bottom right;
          box-shadow: 0 20px 60px -15px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04);

          height: 0;
          opacity: 0;
          transform: scale(0.92) translateY(12px);
          pointer-events: none;
          transition:
            opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
            transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
            height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .rag-panel--open {
          height: 520px;
          opacity: 1;
          transform: scale(1) translateY(0);
          pointer-events: auto;
        }

        /* ── FAB — transparent, the leaf IS the button ─────────────── */
        .rag-fab {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 52px;
          height: 52px;
          padding: 0;
          z-index: 50;
          cursor: pointer;
          border: none;
          border-radius: 0;
          background: transparent;
          -webkit-tap-highlight-color: transparent;
          transition:
            transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
            opacity 0.3s ease,
            filter 0.3s ease;
          filter: drop-shadow(0 3px 8px rgba(0,0,0,0.18));
          
          /* Natural idle breeze animation */
          animation: natural-breeze 7s ease-in-out infinite;
        }
        
        @keyframes natural-breeze {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          33%      { transform: translateY(-3px) rotate(3deg); }
          66%      { transform: translateY(1px) rotate(-2deg); }
        }

        .rag-fab:hover {
          animation: none; /* Pause breeze on hover */
          transform: translateY(-4px) scale(1.04);
          filter: drop-shadow(0 8px 16px rgba(0,0,0,0.22));
        }
        
        .rag-fab:active {
          transform: translateY(-1px) scale(0.95);
          transition-duration: 0.1s;
        }
        
        .rag-fab--hidden {
          transform: scale(0.6);
          opacity: 0;
          pointer-events: none;
          animation: none;
        }

        /* ── Leaf SVG inside FAB ───────────────────────────────────── */
        .rag-fab__leaf {
          width: 100%;
          height: 100%;
          transform-origin: bottom center;
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.4s ease;
        }
        
        .rag-fab:hover .rag-fab__leaf {
          transform: rotate(-6deg) scale(1.06);
          filter: brightness(1.15);
        }
      `}} />
    </motion.div>
  );
}
